import React, { useState, useEffect, useMemo } from 'react';
import ErrorBoundary from './components/common/ErrorBoundary';
import Wizard from './components/Wizard';
import Login from './components/Login';
import LocationSelection from './components/LocationSelection';
import ApplicantDashboard from './components/ApplicantDashboard';
import MonteurDashboard from './components/MonteurDashboard';
import AssetDashboard from './components/AssetDashboard';
import ExecutionView from './components/ExecutionView';

import { AuthProvider, useAuth } from './contexts/AuthContext';
import { DataProvider, useData } from './contexts/DataContext';
import { dataService } from './services/dataService';
import { auth } from './firebase';
import { signOut } from 'firebase/auth';

import logo from './assets/logo.png';
import { restoreHierarchy } from './utils/restoreHierarchy';
import AppRouter from './components/AppRouter';
import ThemeToggle from './components/common/ThemeToggle';

const AdminDashboard = React.lazy(() => import('./components/AdminDashboard'));
const ActiveLOTODashboard = React.lazy(() => import('./components/dashboard/ActiveLOTODashboard'));
const TechDocDashboard = React.lazy(() => import('./components/dashboard/TechDocDashboard'));
const KennisKluis = React.lazy(() => import('./components/KennisKluis'));
import Home from './components/Home';
import Onboarding from './components/Onboarding';

// Simple Fallback Spinner
const LoadingSpinner = () => (
    <div className="flex flex-col items-center justify-center min-h-[50vh]">
        <span className="loading loading-ring loading-lg text-primary mb-4"></span>
        <p className="text-slate-400">Onderdeel laden...</p>
    </div>
);

function AppContent() {
    // --- CONTEXT HOOKS ---
    const { user, loading: authLoading, adminMode, setAdminMode, authLoadingStatus, authError } = useAuth();
    const {
        globalLibrary,
        taskMetadata,
        componentMetadata,
        templateConfig,
        globalTools,
        globalPPEs,
        mechanicChecksLibrary,
        configDisciplines,
        configWaste,
        adminData,
        locationData,
        dataLoading,
        loadingStatus: dataLoadingStatus,
        fetchLocationData,
        setAdminData,
        setLocationData,
        // Setters
        setGlobalLibrary,
        setTaskMetadata,
        setTemplateConfig,
        setGlobalTools,
        setGlobalPPEs,
        setMechanicChecksLibrary,
        setConfigDisciplines,
        setConfigWaste,
        systemSettings, setSystemSettings
    } = useData();

    // --- LOCAL UI STATE ---
    const [selectedLocation, setSelectedLocation] = useState(null);
    const [viewMode, setViewMode] = useState('home'); // 'home', 'wizard', 'applicant_dashboard', 'monteur_dashboard', 'asset_dashboard'
    const [editingPermit, setEditingPermit] = useState(null);
    const [executionPermit, setExecutionPermit] = useState(null);
    const [deepLinkTarget, setDeepLinkTarget] = useState(null); // { assetId }
    const [storedScrollY, setStoredScrollY] = useState(0);
    const [wizardSessionId, setWizardSessionId] = useState(0);
    const [wizardState, setWizardState] = useState(null); // Suspended Wizard State
    const [debugError, setDebugError] = useState(null);

    // DEBUG: Monitor executionPermit changes
    useEffect(() => {
        if (executionPermit) {
            console.log("🛠️ [App] executionPermit updated:", {
                id: executionPermit.id,
                ultimoNr: executionPermit.ultimoNr,
                status: executionPermit.status,
                description: executionPermit.description,
                location: executionPermit.location,
                assignedTo: executionPermit.assignedTo,
                hasLotoSteps: !!(executionPermit.lotoSteps?.length),
                hasChecks: !!(executionPermit.checks?.length),
                fullData: JSON.stringify(executionPermit).substring(0, 500)
            });
        }
    }, [executionPermit]);


    // --- DATA PREPARATION ---
    // Construct wizardData purely from loaded Context Data (Moved up to satisfy Rules of Hooks)
    const wizardData = useMemo(() => ({
        // Base structure expected by Wizard, prioritized from Location Data
        ...locationData,

        // Critical Libraries
        onderdeel_tasks: { ...globalLibrary, ...(locationData?.onderdeel_tasks || locationData?.library || {}) },
        task_metadata: taskMetadata || {},
        template_config: templateConfig || {},

        // Mappings
        task_waste_mapping: {}, // Legacy key, cleared

        // Dynamic Lists
        users: (adminData?.users || []).filter(u => u.allowedLocations && u.allowedLocations.includes(selectedLocation)),
        global_tools: globalTools || [],
        global_ppes: globalPPEs || [],
        mechanic_checks_library: mechanicChecksLibrary || {},
        config_waste: configWaste || [],

        // Personnel comes from Location Data
        personnel: locationData?.personnel || []
    }), [
        locationData,
        globalLibrary,
        taskMetadata,
        templateConfig,
        adminData,
        selectedLocation,
        globalTools,
        globalPPEs,
        mechanicChecksLibrary,
        configWaste
    ]);

    // --- HANDLERS ---
    // Destroy session (delete files) - Used on Cancel/Reset
    const cleanupWizardSession = async () => {
        if (wizardState?.sessionUploadedUrls?.length > 0) {
            for (const url of wizardState.sessionUploadedUrls) {
                try {
                    await dataService.deleteImage(url);
                } catch (err) {
                    console.warn("Failed to delete orphaned session image:", url, err);
                }
            }
        }
        setWizardState(null);
    };

    // Finalize session (keep files) - Used on Submit
    const cleanupAfterSubmit = () => {
        setWizardState(null); // Just clear the state, don't delete files
        setWizardSessionId(prev => prev + 1); // Increment ID to force full reset
    };

    // --- NAVIGATION HANDLERS ---
    const handleHome = () => {
        // Always go home and reset temporary view states, but keep wizard session if possible?
        // User said: "Home... resets everything". So we cleanup.
        cleanupWizardSession();
        setViewMode('home');
        setAdminMode(false);
        setExecutionPermit(null);
        setDeepLinkTarget(null);
    };

    const handleBackSmart = () => {
        // Context-aware back navigation
        if (wizardState && !adminMode) {
            setViewMode('wizard');
        } else {
            setViewMode('home');
            setAdminMode(false);
        }
    };

    // --- HANDLERS ---
    const handleLogout = async () => {
        try {
            await cleanupWizardSession();
            await signOut(auth);
            // Local state reset handled by AuthContext effect
            setSelectedLocation(null);
            setViewMode('home');
        } catch (error) {
            console.error("Logout failed", error);
        }
    };

    const handleLocationSelect = async (locName) => {
        setSelectedLocation(locName);
        try {
            await fetchLocationData(locName);
            // Note: Unlike before, we don't assume we immediately render wizard effectively unless viewMode is wizard.
            // Usually this is called when entering Wizard.
        } catch (error) {
            setDebugError("Fout bij laden locatie: " + error.message);
        }
    };

    const handlePreviewAsset = (arg1, hall, line, subline, photoUrl, instanceName, overrides) => {
        // Handle both Object (new) and Argument List (legacy) signatures
        let data = {};
        if (typeof arg1 === 'object' && arg1 !== null) {
            // New Signature: ({ type, hall, line, subline, photoUrl, name, overrides })
            data = {
                assetId: arg1.type,
                hall: arg1.hall,
                line: arg1.line,
                subline: arg1.subline,
                photoUrl: arg1.photoUrl,
                instanceName: arg1.name, // Note: Hierarchy calls it 'name', App expects 'instanceName'
                overrides: arg1.overrides
            };
        } else {
            // Legacy Signature: (assetId, hall, line, subline, photoUrl, instanceName, overrides)
            data = {
                assetId: arg1,
                hall,
                line,
                subline,
                photoUrl,
                instanceName,
                overrides
            };
        }

        // Store scroll
        setStoredScrollY(window.scrollY);

        setDeepLinkTarget({
            ...data
        });
        setViewMode('asset_dashboard');
        window.scrollTo(0, 0);
    };

    const handleAdminUpdate = (newData) => {
        // Optimistic update wrapper for AdminDashboard
        setAdminData(prev => ({ ...prev, ...newData }));

        // SYNC GLOBAL STATE (Fix for Molen disappearing)
        // If AdminDashboard updates library/tasks, we must update the specific context state
        // so that the 'data' prop passed back to AdminDashboard is accurate.
        if (newData.onderdeel_tasks) setGlobalLibrary(newData.onderdeel_tasks);
        if (newData.task_metadata) setTaskMetadata(newData.task_metadata);
        if (newData.template_config) setTemplateConfig(newData.template_config);
        if (newData.global_tools) setGlobalTools(newData.global_tools);
        if (newData.global_ppes) setGlobalPPEs(newData.global_ppes);
        if (newData.mechanic_checks_library) setMechanicChecksLibrary(newData.mechanic_checks_library);
        if (newData.config_disciplines) setConfigDisciplines(newData.config_disciplines);
        if (newData.config_waste) setConfigWaste(newData.config_waste);
        if (newData.system_settings) setSystemSettings(newData.system_settings);
    };


    // --- AUTO-SELECTION LOGIC ---
    useEffect(() => {
        // Only trigger if we are navigating to the wizard (non-admin view) and haven't selected a location yet
        if (viewMode === 'wizard' && !selectedLocation && !adminMode && user) {
            const isSuperAdmin = user.role === 'superuser';

            // Get available locations based on role
            const availableLocs = isSuperAdmin
                ? (adminData?.locations ? Object.keys(adminData.locations) : [])
                : (user.allowedLocations || []);

            if (availableLocs.length === 1) {
                handleLocationSelect(availableLocs[0]);
            }
        }
    }, [viewMode, selectedLocation, adminMode, user, adminData]);

    // --- RENDER LOGIC ---

    // 1. Loading Screen (Auth or Critical Data)
    if (authLoading) {
        return (
            <div className="min-h-screen bg-slate-900 flex items-center justify-center">
                <div className="text-center">
                    <span className="loading loading-ring loading-lg text-primary mb-4"></span>
                    <p className="text-slate-400 animate-pulse">{authLoadingStatus}</p>
                </div>
            </div>
        );
    }

    // 2. Login Screen
    if (!user) {
        return <Login serverError={authError} />;
    }

    // 2b. Ingelogd maar zonder profiel: nieuw bedrijf starten of aansluiten
    // bij een bestaand team. Zie Onboarding voor waarom dit een keuze is en
    // geen automatische profielaanmaak meer.
    if (user.geenProfiel) {
        return <Onboarding currentUser={user} />;
    }

    // 3. User Loaded -> Check Data Loading
    // Only block if we truly have NO data yet for the layout we need
    if (dataLoading && !globalLibrary) {
        return (
            <div className="min-h-screen bg-slate-900 flex items-center justify-center">
                <div className="text-center">
                    <span className="loading loading-dots loading-lg text-blue-500 mb-4"></span>
                    <p className="text-blue-300 font-medium">{dataLoadingStatus}</p>
                </div>
            </div>
        );
    }

    // --- RENDER LOGIC ---
    let content = null;

    if (viewMode === 'wizard' && !selectedLocation && !adminMode) {
        content = (
            <LocationSelection
                locations={(user.role === 'superuser') ? Object.keys(adminData?.locations || {}) : (user.allowedLocations || [])}
                onSelect={handleLocationSelect}
                onLogout={handleLogout}
                onBack={() => setViewMode('home')}
            />
        );
    } else {
        const isSuperAdmin = user.role === 'superuser';
        const isPrivileged = user.role === 'admin' || user.role === 'superuser';

        content = (
            <div className="min-h-screen bg-slate-900 text-slate-100 selection:bg-blue-500/30">
                {/* Global theme toggle — floats on every screen */}
                <div className="no-print" style={{ position: 'fixed', bottom: '1rem', left: '1rem', zIndex: 60 }}>
                    <ThemeToggle />
                </div>
                <div className="min-h-screen pb-12 relative">
                    {/* Admin Dashboard Overlay */}
                    {isPrivileged && adminMode && viewMode !== 'home' && (
                        <div style={{ display: deepLinkTarget ? 'none' : 'block' }} className="relative">
                            <React.Suspense fallback={<LoadingSpinner />}>
                                <ErrorBoundary>
                                    <AdminDashboard
                                        data={{
                                            onderdeel_tasks: globalLibrary,
                                            task_metadata: taskMetadata,
                                            template_config: templateConfig,
                                            global_tools: globalTools,
                                            global_ppes: globalPPEs,
                                            mechanic_checks_library: mechanicChecksLibrary,
                                            locations: adminData?.locations || {},
                                            users: adminData?.users || [],
                                            config_disciplines: configDisciplines,
                                            config_waste: configWaste,
                                            system_settings: systemSettings
                                        }}
                                        onUpdate={handleAdminUpdate}
                                        currentUser={user}
                                        onPreviewAsset={handlePreviewAsset}
                                        onOpenCleanup={() => setShowCleanup(true)}
                                        headerActions={
                                            <>
                                                <button onClick={() => { setAdminMode(false); setViewMode('home'); }} className="btn btn-sm bg-slate-700 text-slate-200 hover:bg-slate-600 border-slate-600 text-xs shadow-md mr-2">🏠 Home</button>
                                                <button onClick={() => { setAdminMode(false); setViewMode('active_loto_dashboard'); }} className="btn btn-sm btn-error text-xs shadow-lg font-bold animate-pulse mr-2" style={{ backgroundColor: '#dc2626', color: '#ffffff', borderColor: '#b91c1c' }}>⚠️ Active LOTO's</button>
                                                <button onClick={async () => {
                                                    // RBAC FIX: Only superusers get all locations. Admins get their allowedLocations.
                                                    let availableLocs = isSuperAdmin
                                                        ? (adminData?.locations ? Object.keys(adminData.locations) : [])
                                                        : (user.allowedLocations || []);

                                                    if (availableLocs.length === 1) handleLocationSelect(availableLocs[0]);
                                                    else if (availableLocs.length > 1) setSelectedLocation(null);
                                                    setAdminMode(false); setViewMode('wizard');
                                                }} className="btn btn-sm btn-primary text-xs shadow-lg mr-2" style={{ backgroundColor: '#2563eb', color: '#ffffff', borderColor: '#1d4ed8' }}>Naar Wizard</button>
                                                <button onClick={handleLogout} className="btn btn-sm btn-secondary text-xs bg-slate-800 text-white border-slate-600 hover:bg-slate-700 shadow-md">Uitloggen</button>
                                            </>
                                        }
                                    />
                                </ErrorBoundary>
                            </React.Suspense>
                        </div>
                    )}

                    {/* View Modes */}
                    {viewMode === 'home' && <Home currentUser={user} onLogout={handleLogout} onNavigate={(target, reset = false) => {
                        if (reset && target === 'wizard') { cleanupWizardSession(); setEditingPermit(null); setExecutionPermit(null); setWizardSessionId(prev => prev + 1); setDeepLinkTarget(null); }
                        if (target === 'admin_dashboard') { cleanupWizardSession(); setAdminMode(true); setViewMode('wizard'); }
                        else if (target === 'wizard') { setAdminMode(false); setViewMode('wizard'); }
                        else if (target === 'active_loto_dashboard') { cleanupWizardSession(); setAdminMode(false); setViewMode('active_loto_dashboard'); }
                        else if (target === 'applicant_dashboard') { setAdminMode(false); setViewMode('applicant_dashboard'); }
                        else if (target === 'monteur_dashboard') { cleanupWizardSession(); setAdminMode(false); setViewMode('monteur_dashboard'); }
                        else if (target === 'tech_doc_dashboard') { cleanupWizardSession(); setAdminMode(false); setViewMode('tech_doc_dashboard'); }
                        else if (target === 'kennis_kluis') { cleanupWizardSession(); setAdminMode(false); setViewMode('kennis_kluis'); }
                    }} />}

                    {viewMode === 'applicant_dashboard' && <ErrorBoundary><ApplicantDashboard currentUser={user} onBack={handleBackSmart} onHome={handleHome} onEdit={(permit) => { setEditingPermit(permit); setViewMode('wizard'); }} /></ErrorBoundary>}

                    {viewMode === 'monteur_dashboard' && <div className="relative"><ErrorBoundary><MonteurDashboard currentUser={user} onLogout={handleLogout} onBack={handleBackSmart} onHome={handleHome} onClose={handleHome} onOpenExecution={setExecutionPermit} users={adminData?.users || []} /></ErrorBoundary></div>}

                    {viewMode === 'active_loto_dashboard' && <div className="relative z-50"><React.Suspense fallback={<LoadingSpinner />}><ErrorBoundary><ActiveLOTODashboard onBack={handleBackSmart} onHome={handleHome} /></ErrorBoundary></React.Suspense></div>}

                    {viewMode === 'tech_doc_dashboard' && <div className="relative z-50"><React.Suspense fallback={<LoadingSpinner />}><ErrorBoundary><TechDocDashboard onBack={handleBackSmart} onHome={handleHome} /></ErrorBoundary></React.Suspense></div>}

                    {viewMode === 'kennis_kluis' && <div className="relative z-50"><React.Suspense fallback={<LoadingSpinner />}><ErrorBoundary><KennisKluis currentUser={user} onBack={handleBackSmart} onHome={handleHome} /></ErrorBoundary></React.Suspense></div>}

                    {viewMode === 'asset_dashboard' && deepLinkTarget && <div className="relative"><ErrorBoundary><AssetDashboard
                        assetId={deepLinkTarget.assetId}
                        instanceName={deepLinkTarget.instanceName}
                        photoUrl={deepLinkTarget.photoUrl}
                        deepLinkLocation={{ hall: deepLinkTarget.hall, line: deepLinkTarget.line, subline: deepLinkTarget.subline }}
                        location={selectedLocation}
                        hierarchyData={deepLinkTarget.overrides?.hierarchy || locationData?.hierarchy}
                        globalLibrary={deepLinkTarget.overrides?.library || locationData?.onderdeel_tasks || globalLibrary}
                        taskMetadata={deepLinkTarget.overrides?.taskMetadata || locationData?.task_metadata || taskMetadata}
                        componentMetadata={locationData?.component_metadata || componentMetadata}
                        currentUser={user}
                        onBack={() => { setDeepLinkTarget(null); if (!adminMode) setViewMode('wizard'); else setTimeout(() => window.scrollTo(0, storedScrollY), 50); }}
                        onHome={handleHome}
                        onStartWizard={(assetId, options = {}) => {
                            setEditingPermit({
                                component: deepLinkTarget.assetId,
                                hall: deepLinkTarget.hall || '',
                                line: deepLinkTarget.line || '',
                                subline: deepLinkTarget.subline || '',
                                instanceName: deepLinkTarget.instanceName || '',
                                instancePhoto: deepLinkTarget.photoUrl || '',
                                taskImageUrls: deepLinkTarget.photoUrl ? [deepLinkTarget.photoUrl] : [],
                                isNewFromAsset: true,
                                task: options.task || (options.isBreakdown ? 'STORING VERHELPEN' : ''),
                                description: options.isBreakdown ? `SPOED: Storing verhelpen aan ${deepLinkTarget.instanceName || deepLinkTarget.assetId}` : '',
                                isBreakdown: options.isBreakdown || false
                            });
                            setViewMode('wizard');
                            setAdminMode(false);
                            setWizardSessionId(prev => prev + 1);
                        }} /></ErrorBoundary></div>}

                    {executionPermit && (
                        <ErrorBoundary>
                            <ExecutionView
                                permit={executionPermit}
                                onClose={() => setExecutionPermit(null)}
                                currentUser={user}
                                users={adminData?.users || []}
                            />
                        </ErrorBoundary>
                    )}

                    {/* MAIN WIZARD VIEW */}
                    <div style={{ display: (viewMode === 'wizard' && !adminMode) ? 'block' : 'none' }}>
                        <header className="glass-panel border-x-0 border-t-0 rounded-none py-2 md:py-4 mb-4 md:mb-8 no-print sticky top-0 z-40">
                            <div className="container flex flex-col md:flex-row items-center justify-between gap-4 md:gap-8">
                                <div className="flex items-center gap-4 w-full md:w-auto justify-between md:justify-start">
                                    <div className="flex items-center gap-4">
                                        <img src={logo} alt="Raconsoft Logo" className="wizard-logo" />
                                        <div>
                                            <h1 className="text-lg md:text-xl font-bold tracking-tight text-white">RaconWorks</h1>
                                            <p className="text-[10px] md:text-xs text-slate-400">Veilig Werkplan Systeem <span className="ml-2 md:ml-3 px-1.5 md:px-2 py-0.5 rounded-full bg-slate-800 text-blue-400 border border-slate-700 inline-block">{selectedLocation}</span><span className="ml-2 text-slate-500">| {user.username || user.email}</span></p>
                                        </div>
                                    </div>
                                </div>
                                {isPrivileged && !adminMode && (
                                    <div className="grid grid-cols-2 lg:flex gap-2 items-center w-full lg:w-auto mt-2 lg:mt-0">
                                        <button onClick={() => { cleanupWizardSession(); setViewMode('home'); }} className="btn btn-sm h-10 min-h-[40px] bg-slate-700 text-slate-200 hover:bg-slate-600 border-slate-600 text-xs shadow-md w-full lg:w-auto">🏠 Home</button>
                                        {viewMode === 'wizard' && !adminMode && <button onClick={() => setViewMode('applicant_dashboard')} className="btn btn-sm h-10 min-h-[40px] btn-primary text-xs bg-blue-600 hover:bg-blue-500 shadow-lg w-full lg:w-auto">Mijn Aanvragen</button>}
                                        {!adminMode && <button onClick={() => { cleanupWizardSession(); setAdminMode(false); setViewMode('monteur_dashboard'); setExecutionPermit(null); }} className="btn btn-sm h-10 min-h-[40px] btn-secondary text-xs bg-indigo-600 hover:bg-indigo-500 text-white shadow-lg border-none hidden lg:inline-flex w-full lg:w-auto">Naar Monteur View</button>}
                                        {!adminMode && <button onClick={() => { setAdminMode(false); setViewMode('active_loto_dashboard'); }} className="btn btn-sm h-10 min-h-[40px] btn-error text-xs shadow-lg font-bold animate-pulse" style={{ backgroundColor: '#dc2626', color: '#ffffff', borderColor: '#b91c1c' }}>⚠️ Active LOTO's</button>}
                                        {!adminMode && <button onClick={() => { cleanupWizardSession(); setAdminMode(true); setExecutionPermit(null); setDeepLinkTarget(null); setViewMode('wizard'); }} className="btn btn-sm h-10 min-h-[40px] btn-primary text-xs bg-purple-600 hover:bg-purple-500 shadow-lg w-full lg:w-auto">Terug naar Admin</button>}
                                        <button onClick={handleLogout} className="btn btn-sm h-10 min-h-[40px] btn-secondary text-xs bg-slate-800 text-white border-slate-600 hover:bg-slate-700 shadow-md col-span-2 lg:col-span-1 w-full lg:w-auto">Uitloggen</button>
                                    </div>
                                )}
                                {!isPrivileged && (
                                    <div className="grid grid-cols-2 lg:flex gap-2 items-center w-full lg:w-auto mt-2 lg:mt-0">
                                        <button onClick={() => { cleanupWizardSession(); setViewMode('home'); }} className="btn btn-sm h-10 min-h-[40px] bg-slate-700 text-slate-200 hover:bg-slate-600 border-slate-600 text-xs shadow-md w-full lg:w-auto">🏠 Home</button>
                                        <button onClick={() => setViewMode('active_loto_dashboard')} className="btn btn-sm h-10 min-h-[40px] btn-error text-xs bg-red-600 text-white border-red-700 hover:bg-red-500 shadow-lg font-bold animate-pulse w-full lg:w-auto">⚠️ Active LOTO's</button>
                                        <button onClick={handleLogout} className="btn btn-sm h-10 min-h-[40px] btn-secondary text-xs bg-slate-800 text-white border-slate-600 hover:bg-slate-700 shadow-md col-span-2 lg:col-span-1 w-full lg:w-auto">Uitloggen</button>
                                    </div>
                                )}
                            </div>
                        </header>
                        <main className="container">
                            <ErrorBoundary>
                                <Wizard
                                    key={`${wizardSessionId}-${selectedLocation || 'default'}`}
                                    data={wizardData}
                                    locationName={selectedLocation}
                                    currentUser={user}
                                    initialData={wizardState || editingPermit}
                                    persistedState={wizardState}
                                    onPersist={setWizardState}
                                    availableLocations={isSuperAdmin ? (adminData?.locations ? Object.keys(adminData.locations) : []) : (user.allowedLocations || [])}
                                    onUpdateLocation={handleLocationSelect}
                                />
                            </ErrorBoundary>
                        </main>
                    </div>
                </div>
            </div>
        );
    }


    return (
        <div className="min-h-screen bg-slate-900 text-slate-100 relative">
            {content}
        </div>
    );
}

// MAIN APP CONTAINER
function App() {
    return (
        <AuthProvider>
            <DataProvider>
                <AppContent />
            </DataProvider>
        </AuthProvider>
    );
}

export default App;
